import React, { useState, useEffect, useRef } from 'react';
import { useAuthRedirect } from '../utils/authRedirect';
import { useUser } from '../context/UserContext';
import AdSenseAd from './AdSenseAd';

function RecipeExtractor() {
    const [input, setInput] = useState('');
    const [recipe, setRecipe] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [title, setTitle] = useState('');
    const [saveMessage, setSaveMessage] = useState('');
    const [saveInitiator, setSaveInitiator] = useState(null);

    const [isMobile, setIsMobile] = useState(false);
    const titleInputRef = useRef(null);

    useEffect(() => {
        if (recipe && titleInputRef.current) {
            titleInputRef.current.focus();
        }
    }, [recipe]);

    const handleAuthError = useAuthRedirect();
    const { user, loading: userLoading } = useUser();
    const shouldShowAds = !userLoading && user?.hasNoAds === false;

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia('(max-width: 767px)');
        const update = (e) => setIsMobile(e.matches);
        setIsMobile(mq.matches);
        try {
            mq.addEventListener('change', update);
        } catch (e) {
            // some browsers use addListener
            try { mq.addListener(update); } catch (err) {}
        }
        return () => {
            try { mq.removeEventListener('change', update); } catch (e) { try { mq.removeListener(update); } catch (err) {} }
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setRecipe(null);
        setSaveMessage('');
        setSaveInitiator(null);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/extract-recipe', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token,
                },
                body: JSON.stringify({ input }),
            });

            if (handleAuthError(response.status)) {
                return; // Redirected, stop further processing
            }

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Something went wrong during extraction.');
            }

            const data = await response.json();
            const normalized = {
                ingredients: data.ingredients || [],
                instructions: data.instructions || [],
                notes: data.notes || [],
                title: data.title || '',
            };
            setRecipe(normalized);
            setTitle(normalized.title);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (initiator) => {
        const trimmedTitle = title.trim();

        if (!trimmedTitle) {
            setSaveInitiator(initiator);
            setSaveMessage('Please add a title before saving.');
            return;
        }

        setSaveInitiator(initiator);
        setSaveMessage('');
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/recipes', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token,
                },
                body: JSON.stringify({ ...recipe, title: trimmedTitle, url: input }),
            });

            if (handleAuthError(response.status)) {
                return; // Redirected, stop further processing
            }

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Something went wrong while saving.');
            }

            setSaveMessage('Recipe saved successfully!');
        } catch (err) {
            setSaveMessage(err.message);
        }
    };

    const handleTitleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave('enter');
        }
    };

    return (
        <div className="page page-extractor">
            <header className="page-header">
                <span className="page-eyebrow">Workspace</span>
                <h1>Extract a recipe instantly</h1>
                <p className="page-subtitle">Paste any recipe URL or text and turn it into a clean, structured checklist for your kitchen.</p>
            </header>

            <div className="extractor-grid">
                {shouldShowAds && !isMobile && <AdSenseAd className="ad-slot--sidebar" minHeight={280} />}

                <section className="panel">
                    <h2>Recipe input</h2>
                    <p className="text-muted">We will fetch everything you need and ignore the fluff. You can only extract one website at a time.</p>
                    <form onSubmit={handleSubmit} className="extractor-form">
                        <label htmlFor="recipeInput" className="form-label">Paste recipe URL or text</label>
                        <textarea
                            id="recipeInput"
                            aria-label="Recipe input: paste a recipe URL or text"
                            className="form-control"
                            rows="6"
                            placeholder="https://cookblog.example/your-favorite-recipe"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            required
                        ></textarea>
                        <div className="extractor-actions">
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {loading ? 'Extracting...' : 'Get recipe'}
                            </button>
                        </div>
                    </form>

                    <div aria-live="polite" className="sr-only">
                        {loading ? 'Extracting recipe...' : recipe ? 'Recipe extracted' : ''}
                    </div>
                </section>

                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                {recipe && (
                    <div className="card recipe-card extractor-results">
                        <div className="card-header">
                            {saveInitiator === 'enter' && saveMessage && (
                                <div className="alert alert-info mb-3" role="status">
                                    {saveMessage}
                                </div>
                            )}
                            <label htmlFor="recipeTitle" className="form-label">Recipe title</label>
                            <input
                                ref={titleInputRef}
                                id="recipeTitle"
                                type="text"
                                className="form-control"
                                placeholder="Give your recipe a memorable name"
                                value={title}
                                onChange={(e) => {
                                    setTitle(e.target.value);
                                    if (saveMessage) {
                                        setSaveMessage('');
                                    }
                                }}
                                onKeyDown={handleTitleKeyDown}
                            />
                        </div>

                        {shouldShowAds && isMobile && (
                            <div className="mb-3">
                                <AdSenseAd className="ad-slot--inline" minHeight={160} />
                            </div>
                        )}

                        <div className="card-body">
                            {recipe.ingredients && recipe.ingredients.length > 0 && (
                                <div className="mb-4">
                                    <h3>Ingredients</h3>
                                    <ul className="list-group">
                                        {recipe.ingredients.map((item, index) => (
                                            <li key={index} className="list-group-item">{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {recipe.instructions && recipe.instructions.length > 0 && (
                                <div className="mb-4">
                                    <h3>Instructions</h3>
                                    <ol className="list-group">
                                        {recipe.instructions.map((item, index) => (
                                            <li key={index} className="list-group-item">{item}</li>
                                        ))}
                                    </ol>
                                </div>
                            )}

                            {(recipe.ingredients.length === 0 && recipe.instructions.length === 0) && (
                                <p className="text-muted">Could not extract specific ingredients or instructions. Please try a different input or format.</p>
                            )}
                        </div>
                        <div className="card-footer">
                            {saveInitiator === 'button' && saveMessage && (
                                <div className="alert alert-info mb-3" role="status">
                                    {saveMessage}
                                </div>
                            )}
                            <div className="extractor-actions">
                                <button className="btn btn-success" onClick={() => handleSave('button')} disabled={!title.trim()}>
                                    Save recipe
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default RecipeExtractor;
